import { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';

import { EMAIL } from 'constants/messageMediums';
import {
  selectIsCampaignPerformanceAlertsFeatureFlagOn,
  selectIsQuietHoursFeatureFlagOn,
} from 'core/userContext';
import { SearchableText } from 'ui-beta/searchableText';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { ARROW_DIRECTION_DOWN, CollapsibleContentArrow } from 'ui/collapsibleContent';
import { AiColor16, ExperimentFlask, QuietHours } from 'ui/icons';
import { Bell } from 'ui/icons/alert/Bell';
import { Link } from 'ui/links';
import { TAG_SIZES } from 'ui/tags';
import { colors } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { isTriggeredByEligibility } from 'utils/campaigns';
import {
  useCampaignAnalyticsReactificationFeature,
  useReactCampaignV1Feature,
} from 'utils/hooks/featureFlags';
import { to12HourClock } from 'utils/time';

import { useExperimentTakeover } from 'modules/experiments/takeover';
import { ExperimentTakeoverSource } from 'modules/experiments/takeover/constants';

import { useIsModernizedSegmentationPageEnabled } from 'modules/segmentation/hooks';
import { getTemplateTypeLabel } from 'modules/campaigns/lib/utils/template';
import { useQueryPlacementById } from 'modules/placements/hooks/useQueryPlacementById';
import { useCampaignFiltersContext } from '../campaignFolders/contexts/CampaignFiltersContext';
import { aiMap, experimentMap, messageMediumMap } from '../constants';
import type { Campaign } from '../domainTypes';
import { getCampaignLink, truncateText } from '../utils';
import {
  ArrowButton,
  CampaignIcons,
  CollapsibleNameCell,
  Content,
  Details,
  IconWrapper,
  NameLink,
  PrimaryCaption,
  RecurringCampaignLink,
  SecondaryCaption,
  StyledCollapsibleContent,
  StyledTagGroup,
} from './CampaignNameCell.styles';
import { CampaignNameNav } from './CampaignNameNav';

const formatQuietHoursTimes = (startTime: string, endTime: string) => {
  const start = to12HourClock(startTime);
  const end = to12HourClock(endTime);

  return `${start} and ${end}`;
};

const content = {
  alertsTooltip: 'Alerts are enabled for this campaign.',
  aiTooltip: 'This campaign is powered by Iterable AI. ',
  campaignID: 'ID',
  eligibilityList: 'Eligibility list',
  experiment: 'Experiment',
  experimentTooltip: 'This campaign is associated with an experiment that tests ',
  fromWorkflow: 'From Journey',
  message: 'Message',
  quietHoursTooltip: 'This campaign uses Quiet Hours. Sends will be paused between',
  sendLists: 'Send Lists',
  subject: 'Subject',
  suppressed: 'Suppressed',
};

const experimentTypes = {
  sendTimeOptimization: 'SendTimeOptimization',
};

type Props = {
  campaign: Campaign;
  groupRecurringCampaigns?: boolean;
};

export const CampaignNameCell = ({ campaign, groupRecurringCampaigns = false }: Props) => {
  const { searchQuery } = useCampaignFiltersContext();
  const isReactCampaignV1FeatureOn = useReactCampaignV1Feature();
  const isModernizedSegmentationPageEnabled = useIsModernizedSegmentationPageEnabled();
  const isCampaignAnalyticsReactificationFeatureOn = useCampaignAnalyticsReactificationFeature();
  const { getExperimentLinkAttributes } = useExperimentTakeover();
  const { placement } = useQueryPlacementById(campaign.placementId);

  const isQuietHoursFeatureEnabled = useSelector(selectIsQuietHoursFeatureFlagOn);
  const isCampaignPerformanceAlertsFeatureEnabled = useSelector(
    selectIsCampaignPerformanceAlertsFeatureFlagOn,
  );

  const [isCollapsed, setIsCollapsed] = useState(true);
  const {
    customQuietHours: quietHours,
    dataScienceOptimizations = [],
    experiments,
    id: campaignId,
    lists = [],
    messageMedium,
    name: campaignName,
    suppressionLists = [],
    templateContentPreview,
    workflow,
    campaignAlertsEnabled,
    templateType,
  } = campaign;

  const showAlerts = !!campaignAlertsEnabled && isCampaignPerformanceAlertsFeatureEnabled;
  const showSTO = !!dataScienceOptimizations?.length;
  const showQuietHours = isQuietHoursFeatureEnabled && !!quietHours;
  const showExperiments = !!experiments?.length;
  const showCampaignIcons = showAlerts || showSTO || showQuietHours || showExperiments;

  const renderCampaignName = isReactCampaignV1FeatureOn ? (
    <CampaignNameNav
      campaign={campaign}
      data-test="campaign-name"
      isCampaignReactificationFeatureOn={isReactCampaignV1FeatureOn}
      isCampaignAnalyticsReactificationFeatureOn={isCampaignAnalyticsReactificationFeatureOn}
    >
      <SearchableText text={campaignName} searchText={searchQuery} isCaseSensitive={false} />
    </CampaignNameNav>
  ) : (
    <NameLink data-test="campaign-name" href={getCampaignLink(campaign)}>
      <SearchableText text={campaignName} searchText={searchQuery} isCaseSensitive={false} />
    </NameLink>
  );

  const nameContent = groupRecurringCampaigns ? (
    <RecurringCampaignLink id={campaign.id} name={campaign.name} />
  ) : (
    renderCampaignName
  );

  // Tooltip Content: When an experiment contains SendTimeOptimization, the tooltip will show a different message then it would if SendTimeOptimization is simply enabled.
  const tooltipContent = experiments?.find(
    (experiment) => experiment.experimentType === experimentTypes.sendTimeOptimization,
  )
    ? aiMap.SendTimeOptimizationExperiment
    : aiMap[dataScienceOptimizations[0]];

  const quietHoursRange = showQuietHours
    ? formatQuietHoursTimes(quietHours?.startTime ?? '', quietHours?.endTime ?? '')
    : '';

  return (
    <Fragment key={campaign.id}>
      <CollapsibleNameCell data-test={`campaign-${campaign.id}-name-cell`}>
        <Content data-test="campaign-content">
          {nameContent}
          <Details data-test="campaign-medium-row">
            {/* Message medium */}
            <SecondaryCaption data-test="campaign-medium">
              {messageMediumMap[messageMedium]}
            </SecondaryCaption>

            {/* Campaign id */}
            <SecondaryCaption data-test="campaign-id">{`${content.campaignID}: ${campaignId}`}</SecondaryCaption>

            {/* Campaign icons */}
            {showCampaignIcons && (
              <CampaignIcons>
                {showAlerts && (
                  <IconWrapper>
                    <Tooltip
                      data-test="campaign-alerts-icon"
                      tooltipContent={`${content.alertsTooltip}`}
                    >
                      <Bell color={colors.grey60} width={16} height={16} />
                    </Tooltip>
                  </IconWrapper>
                )}
                {showSTO && (
                  <IconWrapper>
                    <Tooltip
                      data-test="campaign-icon-ai"
                      tooltipContent={`${content.aiTooltip}${tooltipContent}`}
                    >
                      <AiColor16 />
                    </Tooltip>
                  </IconWrapper>
                )}
                {showQuietHours && (
                  <IconWrapper>
                    <Tooltip
                      data-test="campaign-icon-quiet-hours"
                      tooltipContent={`${content.quietHoursTooltip} ${quietHoursRange}`}
                    >
                      <QuietHours width={16} height={16} />
                    </Tooltip>
                  </IconWrapper>
                )}
                {showExperiments && (
                  <IconWrapper>
                    <Tooltip
                      tooltipContent={`${content.experimentTooltip}${
                        experimentMap[experiments[0].experimentType]
                      }`}
                    >
                      <ExperimentFlask data-test="campaign-icon-exp" />
                    </Tooltip>
                  </IconWrapper>
                )}
              </CampaignIcons>
            )}

            {/* Campaign labels */}
            {!!campaign.labels?.length && (
              <StyledTagGroup
                value={campaign.labels?.map(({ name }) => name)}
                tagSize={TAG_SIZES.SMALL}
                maxTags={2}
              />
            )}
          </Details>
        </Content>
        <ArrowButton
          onClick={() => setIsCollapsed(!isCollapsed)}
          data-test="collapsible-arrow-button"
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.GHOST}
        >
          <CollapsibleContentArrow
            isCollapsed={isCollapsed}
            arrowStart={ARROW_DIRECTION_DOWN}
            arrowEnd={180}
            width={16}
            height={16}
          />
        </ArrowButton>
      </CollapsibleNameCell>
      <StyledCollapsibleContent
        isCollapsed={isCollapsed}
        data-test={`collapsible-campaign-name${isCollapsed ? '-collapsed' : ''}`}
      >
        {/* Placement name for eligibility campaign */}
        {placement?.placementName && (
          <div data-test="campaign-detail">
            <SecondaryCaption data-test="campaign-detail-key">Placement: </SecondaryCaption>
            <PrimaryCaption data-test="campaign-detail-value">
              {placement.placementName}
            </PrimaryCaption>
          </div>
        )}

        {/* Template type for WhatsApp */}
        {!!templateType && (
          <div data-test="campaign-detail">
            <SecondaryCaption data-test="campaign-detail-key">Template type: </SecondaryCaption>
            <PrimaryCaption data-test="campaign-detail-value">
              {getTemplateTypeLabel(templateType)}
            </PrimaryCaption>
          </div>
        )}

        {/* Template preview (subject line or message) */}
        {templateContentPreview && (
          <div data-test="campaign-detail">
            <SecondaryCaption data-test="campaign-detail-key">
              {messageMedium === EMAIL ? `${content.subject}: ` : `${content.message}: `}
            </SecondaryCaption>
            <PrimaryCaption data-test="campaign-detail-value">
              {truncateText(templateContentPreview, 50)}
            </PrimaryCaption>
          </div>
        )}

        {/* Send lists */}
        {lists.length > 0 && (
          <div data-test="campaign-detail">
            <SecondaryCaption data-test="campaign-detail-key">
              {`${
                isTriggeredByEligibility(campaign) ? content.eligibilityList : content.sendLists
              }: `}
            </SecondaryCaption>
            <PrimaryCaption data-test="campaign-detail-value">
              {lists.map(({ id, name }, idx) => (
                <Fragment key={id}>
                  <Link
                    size="caption"
                    inTable
                    key={`list-link-${id}`}
                    href={`/segmentation?emailListId=${id}`}
                    useReactRouter={isModernizedSegmentationPageEnabled}
                  >
                    {name}
                    {` (${id})`}
                  </Link>
                  {idx !== lists.length - 1 && ', '}
                </Fragment>
              ))}
            </PrimaryCaption>
          </div>
        )}

        {/* Suppression lists */}
        {suppressionLists.length > 0 && (
          <div data-test="campaign-detail">
            <SecondaryCaption data-test="campaign-detail-key">
              {`${content.suppressed}: `}
            </SecondaryCaption>
            <PrimaryCaption data-test="campaign-detail-value">
              {suppressionLists.map(({ id, name }, idx) => (
                <Fragment key={id}>
                  <Link
                    size="caption"
                    inTable
                    key={`list-link-${id}`}
                    href={`/segmentation?emailListId=${id}`}
                    useReactRouter={isModernizedSegmentationPageEnabled}
                  >
                    {name}
                    {` (${id})`}
                  </Link>
                  {idx !== suppressionLists.length - 1 && ', '}
                </Fragment>
              ))}
            </PrimaryCaption>
          </div>
        )}

        {/* Experiments */}
        {experiments && (
          <div data-test="campaign-detail">
            <SecondaryCaption data-test="campaign-detail-key">
              {`${content.experiment}: `}
            </SecondaryCaption>
            <PrimaryCaption data-test="campaign-detail-value">
              {experiments.map((experiment, idx) => {
                const { id, name } = experiment;
                const experimentAttributes = getExperimentLinkAttributes({
                  source: ExperimentTakeoverSource.CAMPAIGN_INDEX,
                  experiment,
                  campaign,
                });
                return (
                  <Fragment key={id}>
                    (
                    <Link
                      size="caption"
                      inTable
                      href={`experiment-link-${id}`}
                      data-test={'experiment-link-campaign-index'}
                      {...experimentAttributes}
                    >
                      {name}
                      {` ${id}`}
                    </Link>
                    ){idx !== experiments.length - 1 && ', '}
                  </Fragment>
                );
              })}
            </PrimaryCaption>
          </div>
        )}

        {/* Workflow */}
        {workflow && (
          <div data-test="campaign-detail">
            <SecondaryCaption data-test="campaign-detail-key">
              {`${content.fromWorkflow}: `}
            </SecondaryCaption>
            <PrimaryCaption data-test="campaign-detail-value">
              <Link size="caption" inTable href={`/workflows/${workflow.id}/edit`}>
                {workflow.name}
              </Link>
            </PrimaryCaption>
          </div>
        )}
      </StyledCollapsibleContent>
    </Fragment>
  );
};
